import React, {
  forwardRef,
  useImperativeHandle,
  useState,
  useEffect,
} from "react";
import PropTypes from "prop-types";
import SFToggleButtonGroup from "../../../toggleButtonGroup";
import { SFCircleIcon } from "../../../icons";
import SFTypography from "../../../typography";
import getStyled from "../../../../hooks/getStyled";
import IconTypeTooltip from "../iconTypeTooltip";
import IconMenu from "../iconMenu";

const { styled } = getStyled();

const ToggleButtonContentWrapper = styled("div")(() => ({
  alignItems: "center",
  display: "flex",
  flexDirection: "row",
  gap: "4px",
  justifyContent: "center",
}));

const IconActionGroup = forwardRef(
  ({ actionDefs = [], onSelectionChange = () => {}, hasActiveMenu = false, onMenuStateChange }, ref) => {
    const [selection, setSelection] = useState(null);

    const [menu, setMenu] = useState({
      open: false,
      anchorEl: null,
    });

    const clickedIconActionDef =
      actionDefs.find((actionDef) => actionDef.name === selection) ?? {};

    //this useEffect is used to manually clear the selection of the clicked icon action
    useEffect(() => {
      if (
        clickedIconActionDef.clearSelection &&
        (clickedIconActionDef.children ?? []).length === 0
      ) {
        setSelection(null);
      }
    }, [clickedIconActionDef]);

    // Make it possible to clear the selection from the parent component
    useImperativeHandle(ref, () => ({
      clearSelection: () => {
        setSelection(null);
      },
    }));

    const handleSelectionChange = (event, newSelection) => {
      setSelection(newSelection);
      const open = !!newSelection;
      setMenu({
        open,
        anchorEl: newSelection ? event.currentTarget : null,
      });
      onSelectionChange(newSelection);
      if (onMenuStateChange) onMenuStateChange(open);
    };

    const shouldRenderMenu = clickedIconActionDef.children?.length > 0;

    const buttonDefs = actionDefs.map((actionDef) => ({
      value: actionDef.name,
      className: actionDef.iconProps?.className,
      ariaLabel: actionDef.iconProps?.tooltip ?? actionDef.name,
      disabled: actionDef.disabled,
      label: (
        <IconTypeTooltip
          tooltip={
            // If the action is disabled and has a reason, show the reason in the tooltip
            actionDef.disabled && actionDef.iconProps?.disabledReason
              ? actionDef.iconProps?.disabledReason
              : // Otherwise, show the tooltip from the iconProps or the name of the action
              actionDef.iconProps?.tooltip ?? actionDef.name
          }
          // Disable tooltip if any menu is open
          disableTooltip={hasActiveMenu}
          // Wrap the children with a span if the action is disabled
          // to show the reason in the tooltip of the disabled icon
          wrapChildrenWithSpan={
            actionDef.disabled && actionDef.iconProps?.disabledReason
          }
        >
          <ToggleButtonContentWrapper>
            {actionDef.iconProps?.icon ?? <SFCircleIcon />}
            {actionDef.iconProps?.label && (
              <SFTypography variant="pageActionBar">
                {actionDef.iconProps.label}
              </SFTypography>
            )}
          </ToggleButtonContentWrapper>
        </IconTypeTooltip>
      ),
      onClick: (event) => {
        // If the button has an onClick handler, call it
        if (actionDef.onClick) {
          actionDef.onClick(event, actionDef.name);
        }
        // Also handle the selection change
        handleSelectionChange(event, actionDef.name);
      },
    }));

    return (
      <>
        <SFToggleButtonGroup
          exclusive
          value={selection}
          onChange={handleSelectionChange}
          buttonDefs={buttonDefs}
        />
        {shouldRenderMenu && (
          <IconMenu
            open={menu.open}
            anchorEl={menu.anchorEl}
            actionDefs={clickedIconActionDef.children}
            handleClose={() => {
              setSelection(null);
              setMenu({ open: false, anchorEl: null });
            }}
            selectedItems={[clickedIconActionDef.selectedChild]}
          />
        )}
      </>
    );
  }
);

export default IconActionGroup;

IconActionGroup.propTypes = {
  // An array of action definitions
  actionDefs: PropTypes.arrayOf(
    PropTypes.shape({
      // The name of the action
      name: PropTypes.string.isRequired,
      // The children of the action (rendered as a menu when the action is clicked)
      children: PropTypes.array,
      // The onClick function for the action
      onClick: PropTypes.func,
      // The icon props for the action
      iconProps: PropTypes.shape({
        // The icon to display for the action
        icon: PropTypes.element,
        // The tooltip to display for the icon
        tooltip: PropTypes.string,
        // The label to display to the right of the icon
        label: PropTypes.string,
        // The label to display in the overflow dropdown menu if the action is overflowing
        overflowLabel: PropTypes.string,
        // Whether to disable the tooltip or not
        disableTooltip: PropTypes.bool,
        // The class name for the icon
        className: PropTypes.string,
        // The reason to display in the tooltip if the action is disabled
        disabledReason: PropTypes.string,
      }),
      // Useful for clearing the selection of the active action
      clearSelection: PropTypes.bool,
    })
  ),
  // The function to call when the selection changes
  onSelectionChange: PropTypes.func,
};
